/**
 * VergleichPage — AHB-Vergleich (side-by-side diff of two formatversions).
 *
 * Picks an "alte" and "neue" Formatversion plus a Format + Prüfidentifikator,
 * fetches both AHB docs (a 404 on either side is treated as empty `lines:[]`),
 * runs `diffAhbLines` and renders the interleaved rows as a two-column-group
 * table: green=added (old side blank), red=removed (new side blank),
 * amber=modified (both sides), no tint=equal — mirroring the AHB-Vergleich
 * screenshot, in the Conuti/Fiori look.
 *
 * The Format/Prüfid dropdowns carry change indicators from the changelog run
 * (`changelog.json`) plus the Prozessname from the Prüfid-Prozessliste; the
 * count chips double as row filters and columns can be hidden via the
 * Spalten-Auswahl.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  Select,
  SelectItem,
  Notice,
} from "@conuti-das/prince-ui";
import { Flex, Spinner } from "@/ui-prince";
import { useRelease, useSyncReleaseFromRoute } from "@/context";
import { ALL } from "@/types/pipeline";
import { regelwerkApi, RegelwerkApiError } from "@/lib/regelwerkApi";
import { diffAhbLines } from "@/lib/ahbDiff";
import { getPruefidNames } from "@/lib/pruefidNames";
import changelogData from "@/data/generated/changelog.json";
import type { ChangelogData } from "@/types/changelog";
import type { AhbDoc, AhbDiff, AhbLine, DiffRow, DiffStatus } from "@/types/regelwerk";
import "./vergleich.css";

/** "202604" → "April 2026 (FV2604)" */
const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];
function fvLabel(fv: string): string {
  const m = /^(\d{4})(\d{2})$/.exec(fv);
  if (!m) return fv;
  const month = MONTHS[Number(m[2]) - 1] ?? m[2];
  return `${month} ${m[1]} (FV${fv.slice(2)})`;
}

const EMPTY_DOC = (fmt: string, pruefid: string): AhbDoc => ({
  pruefid,
  edifact_format: fmt,
  lines: [],
});

/** A 404 means "this pruefid does not exist in that FV" → empty lines. */
async function loadDoc(fv: string, fmt: string, pruefid: string): Promise<AhbDoc> {
  try {
    return await regelwerkApi.getAhbDoc(fv, fmt, pruefid);
  } catch (err) {
    if (err instanceof RegelwerkApiError && err.status === 404) {
      return EMPTY_DOC(fmt, pruefid);
    }
    throw err;
  }
}

const COLS: Array<{ key: keyof AhbLine; label: string; mono?: boolean }> = [
  { key: "segment_group_key", label: "Seg.grp", mono: true },
  { key: "segment_code", label: "Segment", mono: true },
  { key: "data_element", label: "Datenelem.", mono: true },
  { key: "value_pool_entry", label: "Qualifier", mono: true },
  { key: "name", label: "Name" },
  { key: "ahb_expression", label: "Status" },
];

/** CSS tint class for a diff status. Both sides share the same tint; a missing
 *  side is rendered as a tinted-but-empty row, exactly like the screenshot. */
function tintClass(status: DiffRow["status"]): string {
  switch (status) {
    case "added":
      return "cell-added"; // only in NEW → both sides green, OLD cells blank
    case "removed":
      return "cell-removed"; // only in OLD → both sides red, NEW cells blank
    case "modified":
      return "cell-modified";
    default:
      return ""; // equal → no tint
  }
}

// --- change indicators from the changelog run (build-time JSON) -------------
// Counts are an *indicator* ("dieser Lauf hat n Änderungen erfasst"), not the
// exact fvA/fvB diff — that one is what the table itself computes.
const CL_DATA = changelogData as ChangelogData;

function buildChangeCounts(): {
  byFormat: Map<string, number>;
  byPruefid: Map<string, number>;
} {
  const byFormat = new Map<string, number>();
  const byPruefid = new Map<string, number>();
  for (const e of CL_DATA.entries) {
    if (e.doctype !== "ahb" || !e.format) continue;
    byFormat.set(e.format, (byFormat.get(e.format) ?? 0) + 1);
    if (e.pruefid) {
      const k = `${e.format}|${e.pruefid}`;
      byPruefid.set(k, (byPruefid.get(k) ?? 0) + 1);
    }
  }
  return { byFormat, byPruefid };
}

const STATUS_META: Array<{ status: DiffStatus; sign: string; label: string }> = [
  { status: "equal", sign: "=", label: "unverändert" },
  { status: "added", sign: "+", label: "hinzugefügt" },
  { status: "removed", sign: "−", label: "entfernt" },
  { status: "modified", sign: "~", label: "geändert" },
];

export function VergleichPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [fvList, setFvList] = useState<string[]>([]);
  const [fvA, setFvA] = useState<string>(searchParams.get("fvA") ?? "");
  const [fvB, setFvB] = useState<string>(searchParams.get("fvB") ?? "");

  /**
   * Der Vergleich hat zwei Formatversionen auf EINER Achse — die globale Wahl
   * ist ein Wert. Sie führt deshalb die **neue** Seite (B), also die Fassung,
   * die man gerade betrachtet; die alte Seite (A) bleibt lokal und rückt nur
   * aus dem Weg, wenn B sie einholt. Ein geteilter Vergleichs-Link (`?fvB=`)
   * setzt umgekehrt die Kopfzeile nach — Deep-Link zuerst, wie überall.
   */
  const { release } = useRelease();
  useSyncReleaseFromRoute(useRef(searchParams.get("fvB")).current);
  const [fmt, setFmt] = useState<string>(searchParams.get("fmt") ?? "");
  const [pruefid, setPruefid] = useState<string>(searchParams.get("pruefid") ?? "");

  /** ahb map of the NEW FV: { FORMAT: [pruefid...] } */
  const [docsB, setDocsB] = useState<Record<string, string[]>>({});

  const [diff, setDiff] = useState<AhbDiff | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** pruefid → Prozessname (aus der Prüfid-Prozessliste) */
  const [pruefidNames, setPruefidNames] = useState<Map<string, string>>(new Map());

  /** which diff statuses are visible (chips double as toggles) */
  const [shownStatus, setShownStatus] = useState<Set<DiffStatus>>(
    () => new Set<DiffStatus>(["equal", "added", "removed", "modified"]),
  );
  /** which COLS are visible per side */
  const [visibleCols, setVisibleCols] = useState<Set<string>>(
    () => new Set(COLS.map((c) => c.key as string)),
  );

  const changeCounts = useMemo(buildChangeCounts, []);

  // --- load the index of formatversions -----------------------------------
  useEffect(() => {
    let cancelled = false;
    regelwerkApi
      .getDocIndex()
      .then((idx) => {
        if (cancelled) return;
        const list = [...(idx.doctypes?.ahb?.formatversions ?? [])].sort().reverse();
        setFvList(list);
        // default selection: newest as B, second-newest as A
        setFvB((cur) => cur || list[0] || "");
        setFvA((cur) => cur || list[1] || list[0] || "");
      })
      .catch((e) => !cancelled && setError(`Index konnte nicht geladen werden: ${String(e)}`));
    return () => {
      cancelled = true;
    };
  }, []);

  // --- die Kopfzeile führt die neue Seite (B) ------------------------------
  useEffect(() => {
    if (release === ALL || !fvList.includes(release)) return;
    setFvB(release);
    setFvA((cur) => {
      if (cur && cur !== release) return cur;
      // A wäre jetzt B — die nächstältere Fassung ist die einzige Wahl, die
      // einen Vergleich überhaupt noch einen Vergleich sein lässt.
      const older = fvList[fvList.indexOf(release) + 1];
      return older ?? fvList.find((f) => f !== release) ?? cur;
    });
  }, [release, fvList]);

  // --- load format/pruefid options from the NEW FV ------------------------
  useEffect(() => {
    if (!fvB) return;
    let cancelled = false;
    regelwerkApi
      .listDocs(fvB)
      .then((d) => {
        if (cancelled) return;
        const ahb = d.ahb ?? {};
        setDocsB(ahb);
        const formats = Object.keys(ahb).sort();
        // Prefer the first format that has pruefids — APERAK/CONTRL etc. carry
        // none, so defaulting to them shows an empty comparison on load.
        const firstNonEmpty = formats.find((f) => (ahb[f]?.length ?? 0) > 0) ?? formats[0] ?? "";
        setFmt((cur) => (cur && formats.includes(cur) ? cur : firstNonEmpty));
      })
      .catch((e) => !cancelled && setError(`Dokumentliste (${fvB}) fehlgeschlagen: ${String(e)}`));
    return () => {
      cancelled = true;
    };
  }, [fvB]);

  // --- load pruefid → Prozessname names for the NEW FV ---------------------
  useEffect(() => {
    if (!fvB) return;
    let cancelled = false;
    getPruefidNames(fvB).then((m) => {
      if (!cancelled) setPruefidNames(m);
    });
    return () => {
      cancelled = true;
    };
  }, [fvB]);

  const formats = useMemo(() => Object.keys(docsB).sort(), [docsB]);
  const pruefids = useMemo(
    () => [...(docsB[fmt] ?? [])].sort(),
    [docsB, fmt],
  );

  // keep pruefid valid when format/list changes
  useEffect(() => {
    if (pruefids.length && !pruefids.includes(pruefid)) setPruefid(pruefids[0]);
  }, [pruefids, pruefid]);

  // reflect selection in the URL (shareable links)
  useEffect(() => {
    const next: Record<string, string> = {};
    if (fvA) next.fvA = fvA;
    if (fvB) next.fvB = fvB;
    if (fmt) next.fmt = fmt;
    if (pruefid) next.pruefid = pruefid;
    setSearchParams(next, { replace: true });
  }, [fvA, fvB, fmt, pruefid, setSearchParams]);

  // --- fetch both docs + diff ---------------------------------------------
  useEffect(() => {
    if (!fvA || !fvB || !fmt || !pruefid) {
      setDiff(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDiff(null);
    Promise.all([loadDoc(fvA, fmt, pruefid), loadDoc(fvB, fmt, pruefid)])
      .then(([oldDoc, newDoc]) => {
        if (cancelled) return;
        setDiff(diffAhbLines(oldDoc.lines, newDoc.lines));
      })
      .catch((e) => {
        if (cancelled) return;
        setDiff(null);
        setError(`Vergleich fehlgeschlagen: ${String(e)}`);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [fvA, fvB, fmt, pruefid]);

  const ready = fvA && fvB && fmt && pruefid;

  function toggleStatus(s: DiffStatus) {
    setShownStatus((cur) => {
      const next = new Set(cur);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  function toggleCol(key: string) {
    setVisibleCols((cur) => {
      const next = new Set(cur);
      if (next.has(key)) {
        // never allow zero columns
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  const activeCols = COLS.filter((c) => visibleCols.has(c.key as string));
  const visibleRows = useMemo(
    () => (diff ? diff.rows.filter((r) => shownStatus.has(r.status)) : []),
    [diff, shownStatus],
  );

  function formatOptionLabel(f: string): string {
    const n = changeCounts.byFormat.get(f) ?? 0;
    return n > 0 ? `● ${f}` : f;
  }
  function pruefidOptionLabel(p: string): string {
    const name = pruefidNames.get(p);
    const n = changeCounts.byPruefid.get(`${fmt}|${p}`) ?? 0;
    const marker = n > 0 ? "● " : "";
    return name ? `${marker}${p} — ${name}` : `${marker}${p}`;
  }

  return (
    <div className="page vergleich-page">
      <div className="page-header">
        <h2>AHB-Vergleich</h2>
        <div className="page-subtitle">
          Stellt zwei Formatversionen eines Prüfidentifikators Zeile für Zeile gegenüber:
          <span style={{ color: "#14651f", fontWeight: 600 }}> grün = hinzugefügt</span>,
          <span style={{ color: "#8a1c1c", fontWeight: 600 }}> rot = entfernt</span>,
          <span style={{ color: "#8a5a00", fontWeight: 600 }}> gelb = geändert</span>.
          {" "}● in den Dropdowns = im Änderungs-Lauf erfasste Änderungen.
        </div>
      </div>

      <Card>
        <Flex className="vergleich-toolbar">
          <div className="vergleich-field">
            <label htmlFor="cmp-fva">Alte Version</label>
            <Select
              id="cmp-fva"
              style={{ minWidth: "220px" }}
              selectedKey={fvA || null}
              onSelectionChange={(key) => {
                if (key) setFvA(String(key));
              }}
            >
              {fvList.map((fv) => (
                <SelectItem key={fv} id={fv}>
                  {fvLabel(fv)}
                </SelectItem>
              ))}
            </Select>
          </div>

          <div className="vergleich-field">
            <label htmlFor="cmp-fvb">Neue Version</label>
            <Select
              id="cmp-fvb"
              style={{ minWidth: "220px" }}
              selectedKey={fvB || null}
              onSelectionChange={(key) => {
                if (key) setFvB(String(key));
              }}
            >
              {fvList.map((fv) => (
                <SelectItem key={fv} id={fv}>
                  {fvLabel(fv)}
                </SelectItem>
              ))}
            </Select>
          </div>

          <div className="vergleich-field">
            <label htmlFor="cmp-fmt">Format</label>
            <Select
              id="cmp-fmt"
              style={{ minWidth: "200px" }}
              selectedKey={fmt || "__empty__"}
              onSelectionChange={(key) => {
                if (key && key !== "__empty__") setFmt(String(key));
              }}
            >
              {formats.length === 0
                ? [<SelectItem key="__empty__" id="__empty__">—</SelectItem>]
                : formats.map((f) => (
                    <SelectItem key={f} id={f}>
                      {formatOptionLabel(f)}
                    </SelectItem>
                  ))}
            </Select>
          </div>

          <div className="vergleich-field vergleich-field--grow">
            <label htmlFor="cmp-pruefid">Prüfidentifikator</label>
            <Select
              id="cmp-pruefid"
              style={{ minWidth: "280px", width: "100%" }}
              selectedKey={pruefid || "__empty__"}
              onSelectionChange={(key) => {
                if (key && key !== "__empty__") setPruefid(String(key));
              }}
            >
              {pruefids.length === 0
                ? [<SelectItem key="__empty__" id="__empty__">—</SelectItem>]
                : pruefids.map((p) => (
                    <SelectItem key={p} id={p}>
                      {pruefidOptionLabel(p)}
                    </SelectItem>
                  ))}
            </Select>
          </div>

          <div className="vergleich-field">
            <label>Spalten</label>
            <div className="vergleich-col-checks">
              {COLS.map((c) => (
                <label key={c.key as string} className="vergleich-col-check">
                  <input
                    type="checkbox"
                    checked={visibleCols.has(c.key as string)}
                    onChange={() => toggleCol(c.key as string)}
                  />
                  <span>{c.label}</span>
                </label>
              ))}
            </div>
          </div>
        </Flex>
      </Card>

      {error && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="negative">{error}</Notice>
        </div>
      )}

      {loading && (
        <div style={{ margin: "var(--s-4)", display: "flex", alignItems: "center", gap: "var(--s-2)" }}>
          <Spinner active size="m" />
          <span>Lade Vergleich …</span>
        </div>
      )}

      {!loading && !ready && !error && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="info">Wähle alte und neue Formatversion, Format und Prüfidentifikator.</Notice>
        </div>
      )}

      {!loading && ready && diff && (
        <>
          <div className="vergleich-chips" style={{ marginTop: "var(--s-3)" }}>
            {STATUS_META.map(({ status, sign, label }) => (
              <button
                key={status}
                type="button"
                className={[
                  "vergleich-chip",
                  `vergleich-chip--${status}`,
                  shownStatus.has(status) ? "" : "vergleich-chip--off",
                ]
                  .filter(Boolean)
                  .join(" ")}
                title={
                  shownStatus.has(status)
                    ? `${label} ausblenden`
                    : `${label} einblenden`
                }
                aria-pressed={shownStatus.has(status)}
                onClick={() => toggleStatus(status)}
              >
                <span className="chip-sign">{sign}</span> {diff.counts[status]} {label}
              </button>
            ))}
            <span className="vergleich-chips-hint">Klick auf einen Chip blendet die Zeilen ein/aus.</span>
          </div>

          <DiffTable diff={diff} rows={visibleRows} cols={activeCols} fvA={fvA} fvB={fvB} />
        </>
      )}
    </div>
  );
}

function SideCells({
  line,
  status,
  side,
  blank,
  cols,
}: {
  line: AhbLine | null;
  status: DiffRow["status"];
  side: "old" | "new";
  blank: boolean;
  cols: typeof COLS;
}) {
  const tint = tintClass(status);
  return (
    <>
      {cols.map((c, idx) => (
        <td
          key={`${side}-${c.key}`}
          className={[c.mono ? "mono" : "", tint, idx === 0 && side === "new" ? "side-sep" : ""]
            .filter(Boolean)
            .join(" ")}
        >
          {/* On the "missing" side we render an empty (but tinted) cell, exactly
              like the screenshot's fully-colored empty row. */}
          {blank || !line ? "" : String(line[c.key] ?? "")}
        </td>
      ))}
    </>
  );
}

function DiffTable({
  diff,
  rows,
  cols,
  fvA,
  fvB,
}: {
  diff: AhbDiff;
  rows: DiffRow[];
  cols: typeof COLS;
  fvA: string;
  fvB: string;
}) {
  if (diff.rows.length === 0) {
    return (
      <div className="vergleich-table-wrap" style={{ marginTop: "var(--s-2)" }}>
        <div className="vergleich-empty">Keine Zeilen für diesen Prüfidentifikator.</div>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="vergleich-table-wrap" style={{ marginTop: "var(--s-2)" }}>
        <div className="vergleich-empty">
          Alle Zeilen sind über die Status-Chips ausgeblendet.
        </div>
      </div>
    );
  }
  return (
    <div className="vergleich-table-wrap" style={{ marginTop: "var(--s-2)" }}>
      <table className="vergleich-table">
        <thead>
          <tr className="side-head">
            <th colSpan={cols.length}>ALTE VERSION (FV{fvA.slice(2)})</th>
            <th className="side-sep" colSpan={cols.length}>
              NEUE VERSION (FV{fvB.slice(2)})
            </th>
          </tr>
          <tr className="col-head">
            {cols.map((c) => (
              <th key={`old-h-${c.key}`}>{c.label}</th>
            ))}
            {cols.map((c, idx) => (
              <th key={`new-h-${c.key}`} className={idx === 0 ? "side-sep" : ""}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            // added → OLD side blank; removed → NEW side blank.
            const oldBlank = row.status === "added";
            const newBlank = row.status === "removed";
            return (
              <tr key={i}>
                <SideCells line={row.old} status={row.status} side="old" blank={oldBlank} cols={cols} />
                <SideCells line={row.new} status={row.status} side="new" blank={newBlank} cols={cols} />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
